import { ResendReleasePaymentHoldSearchCriteria } from "domain/entity/ResendReleasePaymentHold/ResendReleasePaymentHoldSearchCriteria";
import { releasePaymentHoldActionCodeDropdownOption, releasePaymentHoldActionCodeOictDropdownOption, releasePaymentHoldStatusDropdownOption } from "presentation/constant/DropDownOptions/StaticDropdownOptions";
import { ResendReleasePaymentHoldConstant } from "presentation/constant/ResendReleasePaymentHold/ResendReleasePaymentHoldConstant";
import { useResendReleasePaymentHoldVM } from "presentation/hook/ResendReleasePaymentHold/useResendReleasePaymentHoldVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useResendReleasePaymentHoldTracked } from "presentation/store/ResendReleasePaymentHold/ResendReleasePaymentHoldMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DateRangePickerComponent from "presentation/view/components/DateRangePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { useCallback, useMemo, useState } from "react";
import { FieldType, HPHButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

export const ResendReleasePaymentHoldSearchPanel = () => {
    const SCREEN_CONSTANT = ResendReleasePaymentHoldConstant;
    const [resendReleasePaymentHoldState] = useResendReleasePaymentHoldTracked();
    const resendReleasePaymentHoldVM = useResendReleasePaymentHoldVM();
    const { searchCriteria } = resendReleasePaymentHoldState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    const resetButtonClicked = useCallback(() => {
        resendReleasePaymentHoldVM.onRemoveAllSearchCriteria();
    }, [resendReleasePaymentHoldVM]);

    const searchButtonClicked = useCallback((criteria: ResendReleasePaymentHoldSearchCriteria) => {
        if (!criteria.actionCode && !criteria.docDateFrom && !criteria.docDateTo && !criteria.docNo && !criteria.cntrNo && !criteria.status && !criteria.expBookingNo && !criteria.bookingReqSeqNo) {
            messageBarVM.showError("Fill in at least one of the search criteria.");
            return;
        }
        setIsLoading(true);
        resendReleasePaymentHoldVM.setIsSearchLoading(true);
        resendReleasePaymentHoldVM.onSearch(criteria).then((data) => {
            setIsLoading(false);
            resendReleasePaymentHoldVM.setIsSearchLoading(false);
        }).catch(error => {
            setIsLoading(false);
            resendReleasePaymentHoldVM.setIsSearchLoading(false);
        })
    }, [resendReleasePaymentHoldVM, messageBarVM]);

    const memoDocDateFromTo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "470px", marginBottom: "20px" }}>
            <DateRangePickerComponent
                label={SCREEN_CONSTANT.DOC_DATE_RANGE}
                width='470px'
                fieldNames={{ startField: "docDateFrom", endField: "docDateTo" }}
                dateRange={{ startDate: searchCriteria.docDateFrom, endDate: searchCriteria.docDateTo }}
                onDatesChange={resendReleasePaymentHoldVM.onDateRangeChange}
            />
        </div>
        , [SCREEN_CONSTANT.DOC_DATE_RANGE, resendReleasePaymentHoldVM.onDateRangeChange, searchCriteria.docDateFrom, searchCriteria.docDateTo])

    const memoCntrNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.cntrNo ?? ''}
                fieldValue={searchCriteria?.cntrNo}
                fieldLabel={SCREEN_CONSTANT.CONTAINER_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'cntrNo'}
                maxLength={12}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resendReleasePaymentHoldVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.CONTAINER_NO, resendReleasePaymentHoldVM, searchCriteria?.cntrNo])

    const memoDocNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.docNo?.toString()}
                fieldValue={searchCriteria?.docNo}
                fieldLabel={SCREEN_CONSTANT.DOC_NO}
                isSaveClicked={false}
                isMultipleDropDown={true}
                fieldType={FieldType.TEXT}
                fieldKey={'docNo'}
                maxLength={12}
                isTextFieldCapitalize={true}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resendReleasePaymentHoldVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.DOC_NO, resendReleasePaymentHoldVM, searchCriteria?.docNo])

    const memoBookingReqSeqNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.bookingReqSeqNo?.toString()}
                fieldValue={searchCriteria?.bookingReqSeqNo}
                fieldLabel={SCREEN_CONSTANT.BOOKING_REQ_SEQ_NO}
                isSaveClicked={false}
                isMultipleDropDown={true}
                fieldType={FieldType.TEXT}
                fieldKey={'bookingReqSeqNo'}
                maxLength={12}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resendReleasePaymentHoldVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.BOOKING_REQ_SEQ_NO, resendReleasePaymentHoldVM, searchCriteria?.bookingReqSeqNo])

    const memoExpBookingNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={searchCriteria?.expBookingNo?.toString()}
                fieldValue={searchCriteria?.expBookingNo}
                fieldLabel={SCREEN_CONSTANT.EXP_BOOKING_NO}
                isSaveClicked={false}
                isMultipleDropDown={true}
                fieldType={FieldType.TEXT}
                fieldKey={'expBookingNo'}
                maxLength={12}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resendReleasePaymentHoldVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.EXP_BOOKING_NO, resendReleasePaymentHoldVM, searchCriteria?.expBookingNo])

    const memoActionCode = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                isMultipleDropDown={true}
                readOnlyValue={searchCriteria.actionCode?.join(',')}
                fieldValue={searchCriteria?.actionCode}
                fieldLabel={SCREEN_CONSTANT.ACTION_CODE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'actionCode'}
                maxLength={60}
                options={(operatingUnit === "SAPT" || operatingUnit === "KICT") ? releasePaymentHoldActionCodeDropdownOption : releasePaymentHoldActionCodeOictDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resendReleasePaymentHoldVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.ACTION_CODE, operatingUnit, resendReleasePaymentHoldVM, searchCriteria.actionCode])

    const memoStatus = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "20px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={false}
                isMultipleDropDown={true}
                readOnlyValue={searchCriteria?.status?.join(',')}
                fieldValue={searchCriteria?.status}
                fieldLabel={SCREEN_CONSTANT.STATUS}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'status'}
                maxLength={60}
                options={releasePaymentHoldStatusDropdownOption}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    resendReleasePaymentHoldVM.onSearchFieldChange(fieldKey, fieldValue && fieldValue, fFullValue)} />
        </div>
        , [SCREEN_CONSTANT.STATUS, resendReleasePaymentHoldVM, searchCriteria?.status])




    return <div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
        <div className={'flex-row'}>
            <div className={'flex-row-item flex-row-item-full-width'}>
                <Sidebarheader>
                    <SidebarTitle>{'Search Criteria'}</SidebarTitle>
                    <SidebarActionCross>
                        <IconButton
                            fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={resendReleasePaymentHoldVM.onSearchClick} />
                    </SidebarActionCross>
                </Sidebarheader>
            </div>
        </div>

        {isLoading && <Loader Indicator="Spinner" size="Medium" />}

        <div className={'add-edit-form'} style={{ height: '75vh', maxHeight: '75vh', overflow: 'auto' }}>
            <div className="flex-row-auto">
                <div className={'flex-row-item'}>
                    <CriteriaItemContainer>
                        {memoActionCode}
                        {memoStatus}
                    </CriteriaItemContainer>
                    <CriteriaItemContainer>
                        {memoDocDateFromTo}
                    </CriteriaItemContainer>
                    <CriteriaItemContainer>
                        {memoDocNo}
                        {memoCntrNo}
                    </CriteriaItemContainer>
                    {!(operatingUnit === "SAPT" || operatingUnit === "KICT") &&
                        <CriteriaItemContainer>
                            {memoExpBookingNo}
                            {memoBookingReqSeqNo}
                        </CriteriaItemContainer>}
                </div>
            </div>
        </div>
        <SidebarActionBar>
            <HPHButton label={'Reset'} size={'Small'} theme={'Secondary'} onClick={resetButtonClicked} />
            <HPHButton label={'Search'} size={'Small'} theme={'Primary'} onClick={() => searchButtonClicked(searchCriteria)} />
        </SidebarActionBar>
    </div>
}
